<template>
  <base-btn
    type="tertiary"
    size="small"
    class="mr-2 interaction-button"
    :class="{ dragging: isDragging }"
    draggable="true"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @click="$emit('click')"
  >
    <div class="d-flex align-center justify-center">
      <simple-svg :src="getIconForTheme(option.key)" class="draggable-svg" />
      <span class="ml-2">{{ option.text }}</span>
    </div>
  </base-btn>
</template>

<script>
export default {
  name: 'DraggableInteractionItem',
  props: {
    option: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isDragging: false,
    }
  },
  watch: {
    locale: {
      handler() {
        this.updateText()
      },
      immediate: true,
    },
  },
  mounted() {
    this.$root.$on('changeLanguage', this.updateText)
  },
  methods: {
    getIconForTheme(key) {
      if (!this.$vuetify.theme.dark) {
        return this.option.icon
      }

      const darkModeIcons = {
        button: require('@/assets/button-dark.svg'),
        lead: require('@/assets/form-dark.svg'),
        question: require('@/assets/question-interaction-dark.svg'),
      }

      return darkModeIcons[key] || this.option.icon
    },
    updateText() {
      const typeMapping = {
        button: 'button',
        question: 'quiz',
        lead: 'form',
      }
      const translationType = typeMapping[this.option.key] || this.option.key
      this.option.text = this.$i18next.t(
        `message.pages.timelineVideo.timelineItemsLabels.${translationType}`
      )
      this.option.icon = this.getIconForTheme(this.option.key)
    },
    handleDragStart(event) {
      this.isDragging = true

      const typeMapping = {
        button: 'button',
        question: 'quiz',
        lead: 'form',
      }

      const item = {
        type: typeMapping[this.option.key] || this.option.key,
        data: {},
      }

      const dragData = this.createDragImage()
      event.dataTransfer.setDragImage(
        dragData.element,
        dragData.width / 2,
        dragData.height / 2
      )

      event.dataTransfer.setData('item', JSON.stringify(item))
      event.dataTransfer.effectAllowed = 'move'

      this.$root.$emit('dragStartInteraction', item, event)
    },
    handleDragEnd() {
      this.isDragging = false
      this.$root.$emit('dragEndInteraction')
    },
    createDragImage() {
      const DRAG_WIDTH = 150
      const DRAG_HEIGHT = 32

      const dragImage = document.createElement('div')

      dragImage.style.cssText = `
        position: absolute;
        top: -1000px;
        left: -1000px;
        z-index: 9999;
        font-size: 12px;
        height: ${DRAG_HEIGHT}px;
        font-weight: 500;
        min-width: 120px;
        width: ${DRAG_WIDTH}px;
        border-radius: 4px;
        background-color: #d0d5fb;
        color: #3F55EE;
        border: 1px solid #3F55EE;
        border-left: 10px solid #3F55EE;
        border-right: 10px solid #3F55EE;
        display: flex;
        align-items: center;
        font-family: 'Rubik', sans-serif;
        opacity: 0.9;
        box-shadow: 0 8px 20px rgba(63, 85, 238, 0.3);
        transform: scale(1.02);
      `

      const content = document.createElement('div')
      content.style.cssText = `
        padding: 0 8px;
        height: 100%;
        display: flex;
        align-items: center;
        flex: 1;
        overflow: hidden;
        color: #3F55EE;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-weight: 500;
      `

      const textMapping = {
        button: this.$i18next.t(
          'message.pages.timelineVideo.timelineItemsLabels.button'
        ),
        question: this.$i18next.t(
          'message.pages.timelineVideo.timelineItemsLabels.quiz'
        ),
        lead: this.$i18next.t(
          'message.pages.timelineVideo.timelineItemsLabels.form'
        ),
      }

      content.textContent = textMapping[this.option.key] || this.option.text

      dragImage.appendChild(content)
      document.body.appendChild(dragImage)

      setTimeout(() => {
        if (document.body.contains(dragImage)) {
          document.body.removeChild(dragImage)
        }
      }, 100)

      return { element: dragImage, width: DRAG_WIDTH, height: DRAG_HEIGHT }
    },
  },
}
</script>

<style lang="scss" scoped>
.interaction-button {
  cursor: grab;
  transition: all 0.2s ease;
  transform: translateZ(0);

  &:active {
    cursor: grabbing;
  }

  &.dragging {
    opacity: 0.5;
    transform: scale(0.95) rotate(2deg);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    animation: dragPulse 0.6s ease-in-out infinite alternate;
  }
}

@keyframes dragPulse {
  from {
    transform: scale(0.95) rotate(2deg);
    opacity: 0.5;
  }
  to {
    transform: scale(0.98) rotate(-1deg);
    opacity: 0.7;
  }
}

// Efeito de hover mais suave
.interaction-button::v-deep .v-btn__content {
  transition: transform 0.2s ease;
}

.interaction-button:hover::v-deep .v-btn__content {
  transform: scale(1.02);
}

.interaction-button.dragging::v-deep .v-btn__content {
  transform: scale(0.98);
}
</style>
